import { useEffect, useId, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  describePAApiError,
  getSpecialistFeed,
  saveWeatherProfile,
  searchWeatherCities,
  type PASpecialistFeed,
  type WeatherCity,
  type WeatherTransport,
} from '../../../lib/api/team.js';
import { hapticSuccess, hapticTap } from '../../../lib/native.js';
import { useSkillBack } from '../../../lib/skillBack.js';
import { PageShell } from '../../stage/PageShell.js';
import { Skeleton } from '../../ui/Skeleton.js';
import './clima.css';

type WeatherVisual = 'sun' | 'cloud' | 'rain' | 'storm' | 'fog' | 'cold' | 'wind';

interface WeatherPeriod {
  id: 'morning' | 'afternoon' | 'night' | 'day';
  label: string;
  summary: string;
  visual: WeatherVisual;
  minC: number | null;
  maxC: number | null;
  humidityMin: number | null;
  humidityMax: number | null;
  windDirection: string | null;
  windIntensity: string | null;
}

interface WeatherDay {
  date: string;
  weekday: string;
  summary: string;
  visual: WeatherVisual;
  minC: number | null;
  maxC: number | null;
  humidityMin: number | null;
  humidityMax: number | null;
  sunrise: string | null;
  sunset: string | null;
  periods: WeatherPeriod[];
}

interface WeatherForecast {
  provider: 'inmet';
  place: WeatherCity;
  fetchedAt: string;
  validUntil: string;
  stale: boolean;
  days: WeatherDay[];
}

function readForecast(feed?: PASpecialistFeed): WeatherForecast | null {
  const details = feed?.items.find((item) => item.kind === 'weather_forecast')?.details;
  if (!details || !Array.isArray(details.days) || details.days.length === 0) return null;
  return details as unknown as WeatherForecast;
}

// Ícone de tempo pequeno (cabe em `pgs-ic`), sem animação.
function WeatherIcon({ kind, size = 28 }: { kind: WeatherVisual; size?: number }) {
  const gradientId = useId().replace(/:/g, '');
  return (
    <span className="wv-icon" style={{ ['--icon-size' as never]: `${size}px` }} aria-hidden="true">
      <svg viewBox="0 0 64 64">
        <defs>
          <linearGradient id={`sky-${gradientId}`} x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#fff" />
            <stop offset="1" stopColor="#c8e8f7" />
          </linearGradient>
          <linearGradient id={`sun-${gradientId}`} x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#FFE598" />
            <stop offset="1" stopColor="#F7A94B" />
          </linearGradient>
        </defs>
        {(kind === 'sun' || kind === 'cloud') && (
          <circle cx="25" cy="24" r="11" fill={`url(#sun-${gradientId})`} />
        )}
        {(kind === 'cloud' ||
          kind === 'rain' ||
          kind === 'storm' ||
          kind === 'fog' ||
          kind === 'cold') && (
          <path
            d="M17 43c-6.4 0-10-3.9-10-8.8 0-4.6 3.5-8.2 8.2-8.7C17 18.6 22.4 14 29.1 14c7.4 0 13.2 5.4 14.1 12.3 1-.3 2-.5 3.1-.5 5.8 0 10.5 4.2 10.5 9.4S52.3 44 46.4 44H17Z"
            fill={`url(#sky-${gradientId})`}
            stroke="rgba(74,133,166,.24)"
          />
        )}
        {kind === 'rain' && (
          <g stroke="#4DA9D6" strokeWidth="3" strokeLinecap="round">
            <path d="m21 49-3 6M33 49l-3 6M45 49l-3 6" />
          </g>
        )}
        {kind === 'storm' && (
          <>
            <path d="m34 43-8 11h7l-3 8 12-14h-7l3-5Z" fill="#FFD15A" />
            <path
              d="M18 50l-2 4M48 49l-2 5"
              stroke="#6CBCE0"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </>
        )}
        {kind === 'fog' && (
          <g stroke="#8AB6C8" strokeWidth="2.7" strokeLinecap="round">
            <path d="M13 49h36M18 55h31" />
          </g>
        )}
        {kind === 'cold' && (
          <g stroke="#67B8DE" strokeWidth="2.2" strokeLinecap="round">
            <path d="M32 47v13M26 50l12 7M38 50l-12 7" />
          </g>
        )}
        {kind === 'sun' && (
          <g stroke="#FFC661" strokeWidth="2.4" strokeLinecap="round">
            <path d="M25 5v5M25 38v5M6 24h5M39 24h5M11.5 10.5l3.5 3.5M35 34l3.5 3.5M38.5 10.5 35 14M15 34l-3.5 3.5" />
          </g>
        )}
        {kind === 'wind' && (
          <g fill="none" stroke="#55A9CF" strokeWidth="3" strokeLinecap="round">
            <path d="M8 23h32c8 0 8-10 1-10-4 0-6 2-6 5" />
            <path d="M8 34h43c7 0 7 10 0 10-4 0-6-2-6-5" />
            <path d="M13 45h23" />
          </g>
        )}
      </svg>
    </span>
  );
}

function TransportIcon({ kind }: { kind: WeatherTransport }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  return (
    <svg className="wv-glyph" viewBox="0 0 32 32" aria-hidden="true">
      {kind === 'car' && (
        <g {...common}>
          <path d="m6 18 2-6h16l3 6v6H5v-4c0-1 .3-1.4 1-2Z" />
          <path d="M9 12 11 8h10l3 4" />
          <circle cx="10" cy="24" r="2" />
          <circle cx="23" cy="24" r="2" />
          <path d="M8 18h4m8 0h5" />
        </g>
      )}
      {kind === 'transit' && (
        <g {...common}>
          <rect x="7" y="5" width="18" height="21" rx="5" />
          <path d="M10 9h12v8H10zm0 12h3m6 0h3M9 27l-2 2m16-2 2 2" />
          <circle cx="11" cy="22" r="1" fill="currentColor" />
          <circle cx="21" cy="22" r="1" fill="currentColor" />
        </g>
      )}
      {kind === 'walk' && (
        <g {...common}>
          <circle cx="17" cy="6" r="2.5" />
          <path d="m15 11-3 7 5 3 3 7m-5-12 6 1 3-4m-7 8-6 7" />
        </g>
      )}
      {kind === 'bike' && (
        <g {...common}>
          <circle cx="8" cy="23" r="5" />
          <circle cx="24" cy="23" r="5" />
          <path d="m8 23 5-9h5l6 9m-16 0h9l-4-9-2-3m7 3 3-3m-3 0h5" />
        </g>
      )}
      {kind === 'motorcycle' && (
        <g {...common}>
          <circle cx="8" cy="23" r="4.5" />
          <circle cx="24" cy="23" r="4.5" />
          <path d="M8 23h7l4-7h5m-9 7-4-9h5l4 5m1-7h4M6 16h5" />
        </g>
      )}
    </svg>
  );
}

function PinIcon() {
  return (
    <svg
      className="wv-glyph"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 21s-6-5.6-6-11a6 6 0 0 1 12 0c0 5.4-6 11-6 11Z" />
      <circle cx="12" cy="10" r="2.2" />
    </svg>
  );
}

/** Mínima e máxima no formato curto ("18°–29°"). */
function degrees(minC: number | null, maxC: number | null): string {
  if (minC === null && maxC === null) return 'Sem dado';
  if (minC === null) return `${maxC}°`;
  if (maxC === null) return `${minC}°`;
  return `${minC}°–${maxC}°`;
}

/** Faixa de umidade curta ("40%–80%"); null sem nenhum dado. */
function humidityRange(min: number | null, max: number | null): string | null {
  if (min === null && max === null) return null;
  if (min === null) return `${max}%`;
  if (max === null) return `${min}%`;
  return `${min}%–${max}%`;
}

function humidityLine(min: number | null, max: number | null): string | null {
  if (min === null && max === null) return null;
  if (min === null || max === null) return `umidade de ${min ?? max}%`;
  return `umidade de ${min}% a ${max}%`;
}

function windLine(period: WeatherPeriod): string {
  return [
    period.windIntensity && `vento ${period.windIntensity.toLowerCase()}`,
    period.windDirection,
  ]
    .filter(Boolean)
    .join(', ');
}

function dayLabel(date: string, index: number): string {
  if (index === 0) return 'Hoje';
  if (index === 1) return 'Amanhã';
  const name = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', timeZone: 'UTC' }).format(
    new Date(`${date}T12:00:00Z`),
  );
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function updatedLine(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(iso));
}

function Welcome({ onStart, onBack }: { onStart: () => void; onBack: () => void }) {
  return (
    <PageShell
      kicker="Previsão"
      title="O tempo muda. Seu plano acompanha."
      lead="Eu leio a previsão oficial e trago só o que realmente muda no seu dia."
      onBack={onBack}
    >
      <section className="pgs-section">
        <div className="pgs-row">
          <span className="pgs-ic">
            <WeatherIcon kind="rain" />
          </span>
          <span className="pgs-rowt">
            <b>Seu caminho em primeiro lugar</b>
            <small>Chuva, vento e calor ganham o destaque certo</small>
          </span>
        </div>
        <div className="pgs-row">
          <span className="pgs-ic">
            <WeatherIcon kind="cloud" />
          </span>
          <span className="pgs-rowt">
            <b>Os próximos dias de uma vez</b>
            <small>Mínima, máxima e como fica o céu, dia a dia</small>
          </span>
        </div>
        <div className="pgs-row">
          <span className="pgs-ic">
            <WeatherIcon kind="sun" />
          </span>
          <span className="pgs-rowt">
            <b>Pergunte do seu jeito</b>
            <small>“Levo guarda-chuva?” ou “Como fica amanhã?”</small>
          </span>
        </div>
      </section>
      <div className="pgs-actions wv-actions">
        <button
          className="pgs-btn solid"
          onClick={() => {
            void hapticTap();
            onStart();
          }}
        >
          Escolher minha cidade
        </button>
      </div>
      <p className="wv-source">Você escolhe a cidade. A previsão vem do INMET, a fonte oficial.</p>
    </PageShell>
  );
}

const TRANSPORT: Array<{ id: WeatherTransport; label: string; help: string }> = [
  { id: 'car', label: 'Carro', help: 'Visibilidade no caminho' },
  { id: 'motorcycle', label: 'Moto', help: 'Chuva e vento importam' },
  { id: 'transit', label: 'Transporte', help: 'Tempo entre conexões' },
  { id: 'walk', label: 'A pé', help: 'Mais tempo ao ar livre' },
  { id: 'bike', label: 'Bicicleta', help: 'Piso, chuva e vento' },
];

const SENSITIVITY: Array<[string, string]> = [
  ['rain', 'Chuva'],
  ['heat', 'Calor'],
  ['cold', 'Frio'],
  ['wind', 'Vento'],
  ['humidity', 'Ar seco'],
];

const SETUP_COPY: Record<number, { title: string; lead: string }> = {
  1: {
    title: 'Qual cidade eu acompanho?',
    lead: 'Escolha a cidade da sua rotina. Quando viajar, é só trocar.',
  },
  2: {
    title: 'Como o tempo pesa na sua rotina?',
    lead: 'Conte como você costuma sair. Eu destaco o que faz diferença no caminho.',
  },
  3: {
    title: 'Tudo pronto para sair com você',
    lead: 'Um olhar rápido para decidir melhor antes de abrir a porta.',
  },
};

function Setup({ onSaved, onBack }: { onSaved: () => Promise<void>; onBack: () => void }) {
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [place, setPlace] = useState<WeatherCity | null>(null);
  const [cities, setCities] = useState<WeatherCity[]>([]);
  const [searching, setSearching] = useState(false);
  const [transport, setTransport] = useState<WeatherTransport>('car');
  const [sensitivities, setSensitivities] = useState<string[]>(['rain']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const value = query.trim();
    if (place || value.length < 2) {
      setCities([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      searchWeatherCities(value)
        .then((result) => setCities(result.cities))
        .catch(() => setCities([]))
        .finally(() => setSearching(false));
    }, 230);
    return () => clearTimeout(timer);
  }, [place, query]);

  const canContinue = step === 1 ? Boolean(place) : true;
  const toggleSensitivity = (id: string) =>
    setSensitivities((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );

  async function next() {
    setError('');
    void hapticTap();
    if (step < 3) {
      setStep((value) => value + 1);
      return;
    }
    if (!place) return;
    setSaving(true);
    try {
      await saveWeatherProfile(
        place,
        { transport, sensitivities },
        { morningBrief: false, severeAlerts: false, beforeAppointments: false },
      );
      hapticSuccess();
      await onSaved();
    } catch (err) {
      setError(describePAApiError(err, 'Não consegui salvar agora. Tenta mais uma vez?'));
      setSaving(false);
    }
  }

  const copy = SETUP_COPY[step] ?? SETUP_COPY[1]!;

  return (
    <PageShell kicker="Previsão" title={copy.title} lead={copy.lead} onBack={onBack}>
      <div className="pgs-bar wv-progress">
        <span>Preparando a sua previsão</span>
        <b>{step} de 3</b>
        <i>
          <em style={{ width: `${(step / 3) * 100}%` }} />
        </i>
      </div>

      <div key={step} className="wv-step sk-cascade">
        {step === 1 && (
          <>
            <label className="wv-field">
              <span>Cidade</span>
              <input
                value={place?.label ?? query}
                onChange={(event) => {
                  setPlace(null);
                  setQuery(event.target.value);
                }}
                placeholder="Ex.: Campinas, SP"
                autoComplete="off"
              />
            </label>
            {searching && <p className="wv-hint">Procurando a cidade…</p>}
            {!searching && cities.length > 0 && (
              <div className="pgs-section">
                {cities.map((city) => (
                  <button
                    key={city.id}
                    className="pgs-row wv-choice"
                    onClick={() => {
                      setPlace(city);
                      setQuery(city.label);
                      setCities([]);
                    }}
                  >
                    <span className="pgs-ic">
                      <PinIcon />
                    </span>
                    <span className="pgs-rowt">
                      <b>{city.city}</b>
                      <small>{city.stateName}, Brasil</small>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {place && (
              <div className="pgs-row">
                <span className="pgs-ic">✓</span>
                <span className="pgs-rowt">
                  <b>{place.label}</b>
                  <small>Pronto, este é o céu da sua rotina</small>
                </span>
              </div>
            )}
            <div className="pgs-note">
              <i>i</i>
              <span>
                <b>Você está no comando</b>
                <small>Eu acompanho somente a cidade que você escolher.</small>
              </span>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <section className="pgs-section">
              <h2 className="pgs-h2">Como você costuma sair</h2>
              {TRANSPORT.map((item) => (
                <button
                  key={item.id}
                  className={`pgs-row wv-choice${transport === item.id ? ' on' : ''}`}
                  aria-pressed={transport === item.id}
                  onClick={() => setTransport(item.id)}
                >
                  <span className="pgs-ic">
                    <TransportIcon kind={item.id} />
                  </span>
                  <span className="pgs-rowt">
                    <b>{item.label}</b>
                    <small>{item.help}</small>
                  </span>
                </button>
              ))}
            </section>
            <section className="pgs-section">
              <h2 className="pgs-h2">O que mais te atrapalha?</h2>
              <div className="pgs-chips">
                {SENSITIVITY.map(([id, label]) => (
                  <button
                    key={id}
                    className={`pgs-chip${sensitivities.includes(id) ? ' on' : ''}`}
                    aria-pressed={sensitivities.includes(id)}
                    onClick={() => toggleSensitivity(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </section>
          </>
        )}

        {step === 3 && (
          <>
            <section className="pgs-section">
              <h2 className="pgs-h2">Vou acompanhar</h2>
              <div className="pgs-row">
                <span className="pgs-ic">
                  <PinIcon />
                </span>
                <span className="pgs-rowt">
                  <b>{place?.label}</b>
                  <small>
                    {TRANSPORT.find((item) => item.id === transport)?.label},{' '}
                    {sensitivities.length || 'nenhuma'} preferência
                    {sensitivities.length === 1 ? '' : 's'}
                  </small>
                </span>
              </div>
            </section>
            <section className="pgs-section">
              <h2 className="pgs-h2">O que você recebe</h2>
              <div className="pgs-row">
                <span className="pgs-ic">
                  <WeatherIcon kind="rain" />
                </span>
                <span className="pgs-rowt">
                  <b>Seu caminho em primeiro lugar</b>
                  <small>Chuva, vento e calor ganham o destaque certo</small>
                </span>
              </div>
              <div className="pgs-row">
                <span className="pgs-ic">
                  <WeatherIcon kind="cloud" />
                </span>
                <span className="pgs-rowt">
                  <b>Pergunte do seu jeito</b>
                  <small>“Levo guarda-chuva?” ou “Como fica amanhã?”</small>
                </span>
              </div>
              <div className="pgs-row">
                <span className="pgs-ic">
                  <WeatherIcon kind="sun" />
                </span>
                <span className="pgs-rowt">
                  <b>Um dia mais leve</b>
                  <small>A previsão vira uma orientação simples para a rotina</small>
                </span>
              </div>
            </section>
          </>
        )}
      </div>

      {error && (
        <p className="wv-error" role="alert">
          {error}
        </p>
      )}
      <div className="pgs-actions wv-actions">
        {step > 1 && (
          <button
            className="pgs-btn"
            onClick={() => setStep((value) => value - 1)}
            disabled={saving}
          >
            Anterior
          </button>
        )}
        <button className="pgs-btn solid" disabled={!canContinue || saving} onClick={next}>
          {saving ? 'Buscando a previsão…' : step === 3 ? 'Ativar a previsão' : 'Continuar'}
        </button>
      </div>
    </PageShell>
  );
}

function Dashboard({
  forecast,
  onReconfigure,
  onBack,
}: {
  forecast: WeatherForecast;
  onReconfigure: () => void;
  onBack: () => void;
}) {
  // A previsão só chega aqui com dias (readForecast devolve null sem eles).
  const today = forecast.days[0]!;
  const advice =
    today.visual === 'storm'
      ? 'Trovoadas pedem plano B para atividades externas.'
      : today.visual === 'rain'
        ? 'Proteção para chuva e alguns minutos extras no trajeto.'
        : today.visual === 'wind'
          ? 'Vento forte: redobre a atenção em moto ou bicicleta.'
          : (today.maxC ?? 0) >= 35
            ? 'Calor forte: prefira horários mais amenos e leve água.'
            : (today.humidityMin ?? 100) <= 30
              ? 'Ar seco ao longo do dia: tenha água por perto.'
              : 'Sem impacto forte previsto. Confira a mudança entre os períodos.';
  const humidity = humidityRange(today.humidityMin, today.humidityMax);
  const sunLine = [
    today.sunrise && `nasce às ${today.sunrise}`,
    today.sunset && `se põe às ${today.sunset}`,
  ]
    .filter(Boolean)
    .join(' e ');
  const days = forecast.days.slice(0, 5);

  return (
    <PageShell kicker="Previsão" title={forecast.place.label} onBack={onBack}>
      <div className="pgs-figure">
        <b>{degrees(today.minC, today.maxC)}</b>
        <small>{today.summary}. Mínima e máxima de hoje.</small>
      </div>

      {forecast.stale && (
        <div className="pgs-note">
          <i>!</i>
          <span>
            <b>Mostro o último dado válido</b>
            <small>A previsão oficial não atualizou desde {updatedLine(forecast.fetchedAt)}.</small>
          </span>
        </div>
      )}

      <div className="pgs-note">
        <i>
          <WeatherIcon kind={today.visual} size={22} />
        </i>
        <span>
          <b>O que muda no seu dia</b>
          <small>{advice}</small>
        </span>
      </div>

      {today.periods.length > 0 && (
        <section className="pgs-section">
          <h2 className="pgs-h2">Hoje, por período</h2>
          {today.periods.map((period) => {
            const wind = windLine(period);
            const hasRange = period.minC !== null || period.maxC !== null;
            return (
              <div className="pgs-row" key={period.id}>
                <span className="pgs-ic">
                  <WeatherIcon kind={period.visual} />
                </span>
                <span className="pgs-rowt">
                  <b>{period.label}</b>
                  <small>
                    {period.summary}
                    {wind ? `, ${wind}` : ''}
                  </small>
                </span>
                {hasRange && <span className="pgs-val">{degrees(period.minC, period.maxC)}</span>}
              </div>
            );
          })}
        </section>
      )}

      {(humidity || sunLine) && (
        <section className="pgs-section">
          <h2 className="pgs-h2">Detalhes de hoje</h2>
          {humidity && (
            <div className="pgs-row">
              <span className="pgs-rowt">
                <b>Umidade</b>
                <small>Do momento mais seco ao mais úmido</small>
              </span>
              <span className="pgs-val">{humidity}</span>
            </div>
          )}
          {sunLine && (
            <div className="pgs-row">
              <span className="pgs-rowt">
                <b>Sol</b>
                <small>{sunLine.charAt(0).toUpperCase() + sunLine.slice(1)}</small>
              </span>
            </div>
          )}
        </section>
      )}

      <section className="pgs-section">
        <h2 className="pgs-h2">
          Próximos dias <small>{days.length}</small>
        </h2>
        {days.map((day, index) => {
          const dayHumidity = humidityLine(day.humidityMin, day.humidityMax);
          return (
            <div className="pgs-row" key={day.date}>
              <span className="pgs-ic">
                <WeatherIcon kind={day.visual} />
              </span>
              <span className="pgs-rowt">
                <b>{dayLabel(day.date, index)}</b>
                <small>
                  {day.summary}
                  {dayHumidity ? `, ${dayHumidity}` : ''}
                </small>
              </span>
              <span className="pgs-val">{degrees(day.minC, day.maxC)}</span>
            </div>
          );
        })}
      </section>

      <div className="pgs-note">
        <i>?</i>
        <span>
          <b>Pergunte quando quiser</b>
          <small>“Vai chover amanhã?” ou “Como fica o fim de semana?”</small>
        </span>
      </div>

      <p className="wv-source">
        {forecast.stale ? 'Último dado válido' : 'Previsão atualizada'} em{' '}
        {updatedLine(forecast.fetchedAt)}. Fonte: Instituto Nacional de Meteorologia (INMET).
      </p>
      <div className="pgs-actions">
        <button className="pgs-btn" onClick={onReconfigure}>
          Trocar cidade ou preferências
        </button>
      </div>
    </PageShell>
  );
}

export function ClimaView() {
  const back = useSkillBack();
  const qc = useQueryClient();
  const feedQ = useQuery({
    queryKey: ['pa-specialist-feed', 'clima'],
    queryFn: () => getSpecialistFeed('clima'),
    refetchInterval: (query) => (query.state.data?.generating ? 1800 : false),
  });
  const [started, setStarted] = useState(false);
  const [reconfigure, setReconfigure] = useState(false);
  const configured = feedQ.data?.config?.configured === true;
  const forecast = useMemo(() => readForecast(feedQ.data), [feedQ.data]);

  if (feedQ.isLoading)
    return (
      <PageShell kicker="Previsão" title="Clima" onBack={back}>
        <Skeleton variant="rows" label="Abrindo a previsão" />
      </PageShell>
    );

  if (feedQ.isError || !feedQ.data)
    return (
      <PageShell
        kicker="Previsão"
        title="Não consegui abrir a previsão agora"
        lead="Pode ter sido uma falha passageira. Tento de novo quando você pedir."
        onBack={back}
      >
        <div className="pgs-actions">
          <button className="pgs-btn solid" onClick={() => feedQ.refetch()}>
            Tentar novamente
          </button>
        </div>
      </PageShell>
    );

  if (configured && !reconfigure && forecast)
    return (
      <Dashboard forecast={forecast} onReconfigure={() => setReconfigure(true)} onBack={back} />
    );

  if (configured && !reconfigure && (feedQ.data.generating || !forecast))
    return (
      <PageShell
        kicker="Previsão"
        title="Estou preparando o seu céu"
        lead="Só um instante: o seu primeiro dia já vem vindo."
        onBack={back}
      >
        <Skeleton variant="rows" label="Preparando a previsão" />
      </PageShell>
    );

  if (!started && !reconfigure) return <Welcome onStart={() => setStarted(true)} onBack={back} />;

  return (
    <Setup
      onBack={back}
      onSaved={async () => {
        setReconfigure(false);
        setStarted(false);
        await qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'clima'] });
        await feedQ.refetch();
      }}
    />
  );
}
